import { useEffect, useMemo } from 'react'
import { useParams } from 'react-router-dom'
import { skipToken } from '@reduxjs/toolkit/query'

import Paper from '@mui/material/Paper'
import TableContainer from '@mui/material/TableContainer'
import Typography from '@mui/material/Typography'

import PageContainer from '@/components/ui/containers/PageContainer'
import HeartbeatLoader from '@/components/ui/loaders/HeartbeatLoader'

import StaffsTable from './components/StaffsTable'

import { useGetEstablishmentQuery } from '@/core/services/establishment'

import NotFoundPage from '@/pages/error/NotFoundPage'

import { useAppDispatch, useAppSelector } from '@/redux/rootReducer'
import { createOrdoclicAccount, getStaffs, selectStaffs } from '@/redux/reducers/admin.reducer'
import { getExternalId } from '@/core/models/staff.model'

import { EstablishmentTabEnum, EstablishmentTabs, useEstablishmentTab } from './components/EstablishmentTabs'
import { AdminTabPanel } from './components/AdminTabPanel'

export default function EstablishmentPage() {
  const dispatch = useAppDispatch()
  const { establishmentId } = useParams()

  const staffs = useAppSelector((state) => selectStaffs(state))

  const { data: establishment, isLoading: isLoadingEstablishment } = useGetEstablishmentQuery(establishmentId ?? skipToken)

  const tab = useEstablishmentTab()

  const staffsData = useMemo(
    () =>
      staffs.map((staff) => ({
        id: staff._id,
        cps: staff.cps,
        fullName: `${staff.userInfo.name} ${staff.userInfo.surname}`,
        email: staff.email,
        hasOrdoclicAccount: !!getExternalId(staff.externalAccounts, 'ordoclic', establishmentId),
        status: staff.status,
      })),
    [staffs, establishmentId]
  )

  useEffect(() => {
    if (!establishmentId) return

    dispatch(getStaffs({ establishment: establishmentId }))
  }, [dispatch, establishmentId])

  const handleCreateOrdoclicAccount = (staffId: string) => {
    dispatch(createOrdoclicAccount(staffId))
  }

  if (isLoadingEstablishment) return <HeartbeatLoader />

  if (!establishment) return <NotFoundPage />

  return (
    <PageContainer>
      <Typography variant="h2" sx={{ mb: '30px' }}>
        {establishment.name}
      </Typography>
      <EstablishmentTabs />
      <AdminTabPanel />
      {tab === EstablishmentTabEnum.STAFF && (
        <TableContainer component={Paper}>
          <StaffsTable data={staffsData} onCreateOrdoclicAccount={handleCreateOrdoclicAccount} />
        </TableContainer>
      )}
    </PageContainer>
  )
}
